import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Container,
  Link,
  Alert,
  Snackbar,
  Fade,
  Stack,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { theme, spacing } from '../../theme/theme';

const GradientBox = styled(Box)(() => ({
  background: 'linear-gradient(135deg, var(--primary-purple) 0%, var(--gradient-purple) 50%, var(--primary-purple) 100%)',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  textAlign: 'center',
  padding: theme.spacing(4, 2, 3),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 3, 4),
  },
  zIndex: 1,
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  left: theme.spacing(2),
  top: theme.spacing(4),
  color: '#FFFFFF',
  padding: theme.spacing(1),
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  [theme.breakpoints.up('sm')]: {
    left: theme.spacing(3),
    top: theme.spacing(5),
  },
}));

const StyledCard = styled(Paper)(() => ({
  borderRadius: 24,
  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
  maxWidth: 440,
  width: '100%',
  margin: '0 auto',
  backgroundColor: '#FFFFFF',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
}));

const LoginButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(1.5, 3),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  background: 'linear-gradient(to right, var(--primary-purple), var(--gradient-purple))',
  '&:hover': {
    boxShadow: '0 0 15px var(--accent-gold)',
    transform: 'translateY(-1px)',
  },
  '&:disabled': {
    backgroundColor: '#E0E0E0',
    color: '#999999',
  },
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    padding: theme.spacing(1.25, 2.5),
  },
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#FAFAFA',
    '& fieldset': {
      borderColor: '#E0E0E0',
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderColor: theme.colors.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.colors.primary,
      borderWidth: 2,
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
  },
}));

const LoginScreen = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });
  
  const { signIn, error } = useAuth();

  // 监听AuthContext的error状态变化
  useEffect(() => {
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
    }
  }, [error]);

  const handleEmailLogin = async () => {
    if (!email || !password) {
      setSnackbar({ open: true, message: '请填写所有字段', severity: 'error' });
      return;
    }

    try {
      setIsLoading(true);
      await signIn(email, password);
      // 登录成功，signIn方法会自动导航
    } catch (err: any) {
      // AuthContext会设置错误信息，useEffect会自动显示
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleEmailLogin();
    }
  };

  return (
    <GradientBox>
      <Container 
        maxWidth="sm" 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          minHeight: '100vh',
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Header */}
        <HeaderContainer>
          <BackButton onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="medium" />
          </BackButton>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700, 
              color: '#FFFFFF', 
              mb: 1,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              fontSize: { xs: '1.75rem', sm: '2.25rem', md: '2.75rem' },
            }}
          >
            欢迎回来
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 400,
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
            }}
          >
            登录以继续使用紫微AI
          </Typography>
        </HeaderContainer>

        {/* Login Form */}
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            pb: { xs: 3, sm: 4 },
          }}
        >
          <Fade in timeout={1000}>
            <StyledCard elevation={0}>
              <CardContent 
                sx={{ 
                  padding: { xs: 3, sm: 4 },
                  '&:last-child': { pb: { xs: 3, sm: 4 } },
                }}
              >
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700, 
                    color: theme.colors.primary, 
                    textAlign: 'center', 
                    mb: 2,
                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  }}
                >
                  登录
                </Typography>
                
                <Stack spacing={2}>
                  <StyledTextField
                    fullWidth
                    label="邮箱地址"
                    type="email"
                    value={email}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                    onKeyPress={handleKeyPress}
                    variant="outlined"
                    autoComplete="email"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon sx={{ color: theme.colors.primary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    disabled={isLoading}
                  />
                  
                  <StyledTextField
                    fullWidth
                    label="密码"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    variant="outlined"
                    autoComplete="current-password"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon sx={{ color: theme.colors.primary, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            disabled={isLoading}
                            sx={{ color: theme.colors.primary }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    disabled={isLoading}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate('/auth/forgot-password')}
                      sx={{
                        color: theme.colors.primary,
                        fontWeight: 600,
                        textDecoration: 'none',
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                      disabled={isLoading}
                    >
                      忘记密码？
                    </Link>
                  </Box>
                  
                  <LoginButton
                    fullWidth
                    variant="contained"
                    onClick={handleEmailLogin}
                    disabled={isLoading}
                    size="large"
                  >
                    {isLoading ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      '登录'
                    )}
                  </LoginButton>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'center', 
                      alignItems: 'center',
                      mt: { xs: 2, sm: 3 },
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#666666',
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      }}
                    >
                      还没有账户？{' '}
                    </Typography>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate('/auth/register')}
                      sx={{
                        color: theme.colors.primary,
                        fontWeight: 700,
                        textDecoration: 'none',
                        ml: 0.5,
                        fontSize: { xs: '0.8rem', sm: '0.9rem' },
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                      disabled={isLoading}
                    >
                      立即注册
                    </Link>
                  </Box>
                </Stack>
              </CardContent>
            </StyledCard>
          </Fade>
        </Box>
      </Container>

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </GradientBox>
  );
};

export default LoginScreen;